import { useQuery } from '@tanstack/react-query'
import { getSimilarPropertiesById, type SimilarPropertiesRequest } from '../services/contentService'

// Claves para React Query
export const similarPropertiesKeys = {
  all: ['similarProperties'] as const,
  byId: (id: number, embedId?: string, sameZip?: boolean, limit?: number) => [
    ...similarPropertiesKeys.all,
    'byId',
    id,
    embedId,
    sameZip,
    limit
  ] as const,
}

/**
 * Hook para obtener propiedades similares por ID
 * @param id - ID de la propiedad
 * @param embedId - ID del embed (opcional)
 * @param sameZip - Si debe buscar en el mismo código postal (default: false)
 * @param limit - Límite de propiedades a retornar (default: 5)
 */
export const useSimilarProperties = (
  id: number | null | undefined,
  embedId?: string,
  sameZip: boolean = false,
  limit: number = 5
) => {
  // El query solo se ejecutará cuando id tenga un valor válido
  const enabled = !!id && typeof id === 'number' && id > 0
  
  // Usar un queryKey seguro que funcione incluso cuando id es null/undefined
  const queryKey = id 
    ? similarPropertiesKeys.byId(id, embedId, sameZip, limit)
    : [...similarPropertiesKeys.all, 'byId', null, embedId, sameZip, limit]

  console.log('=== useSimilarProperties Debug ===')
  console.log('id', id, 'type:', typeof id)
  console.log('embedId', embedId)
  console.log('enabled', enabled)

  return useQuery({
    queryKey,
    queryFn: () => {
      console.log('=== useSimilarProperties queryFn executing ===')
      if (!id || typeof id !== 'number' || id <= 0) {
        throw new Error('Property ID is required and must be a positive number')
      }
      const request: SimilarPropertiesRequest = {
        id,
        embedId,
        sameZip,
        limit
      }
      console.log('Calling getSimilarPropertiesById with request:', request)
      return getSimilarPropertiesById(request)
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
    gcTime: 10 * 60 * 1000, // 10 minutos en memoria
    retry: (failureCount, error: any) => {
      // No reintentar si es error de autenticación
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 2
    },
    refetchOnWindowFocus: false,
  })
}

export default useSimilarProperties

